import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth0 } from "@auth0/auth0-react";
import logo from "../../assets/logo/defra.png";

const NAV_ITEMS = [
  {
    label: "Dashboard",
    path: "/admin/dashboard",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    label: "Client",
    path: "/admin/client",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
      </svg>
    ),
  },
  {
    label: "Project",
    path: "/admin/project",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    label: "Service",
    path: "/admin/service",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    label: "Blog",
    path: "/admin/blog",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
];

// Decode role dari JWT token (tanpa fetch ke backend)
function getCurrentRole() {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.role || null;
  } catch {
    return null;
  }
}

const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const currentRole = getCurrentRole();

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    logout({ returnTo: window.location.origin });
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  const NavLinkItem = ({ item }) => (
    <Link
      to={item.path}
      onClick={() => setOpen(false)}
      className={`group relative flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all no-underline hover:no-underline ${
        isActive(item.path)
          ? "bg-gradient-to-r from-brand-600 to-eco-600 text-white shadow-lg shadow-brand-500/30"
          : "text-slate-300 hover:text-white hover:bg-white/5"
      }`}
    >
      {isActive(item.path) && (
        <motion.span
          layoutId="active-pill"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-7 bg-white rounded-r-full"
        />
      )}
      <span className={isActive(item.path) ? "text-white" : "text-brand-400 group-hover:text-brand-300"}>
        {item.icon}
      </span>
      <span className="flex-1">{item.label}</span>
      {isActive(item.path) && <span className="text-white/80">→</span>}
    </Link>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 flex items-center justify-between bg-slate-900 text-white px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <img src={logo} alt="Defra" className="h-9 w-auto" />
          <span className="font-oswald-header text-base">DEFRA ADMIN</span>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-72 lg:w-80 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white flex-shrink-0 md:sticky md:top-0 md:h-screen md:overflow-y-auto border-r border-slate-800">
        {/* Brand */}
        <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-800/60">
          <img src={logo} alt="Defra" className="h-12 w-auto" />
          <div>
            <div className="font-oswald-header text-base text-white">
              DEFRA
            </div>
            <div className="text-xs text-slate-400 font-overpass">
              Admin Panel
            </div>
          </div>
        </div>

        {/* Section label */}
        <div className="px-6 pt-6 pb-2 text-[10px] font-bold tracking-widest text-slate-500 uppercase">
          Menu Utama
        </div>

        {/* Nav */}
        <nav className="px-3 space-y-1 flex-1">
          {NAV_ITEMS.map((item) => (
            <NavLinkItem key={item.path} item={item} />
          ))}

          {/* Menu Users — hanya super_admin */}
          {currentRole === "super_admin" && (
            <>
              <div className="px-3 pt-4 pb-1 text-[10px] font-bold tracking-widest text-slate-600 uppercase">
                Pengaturan
              </div>
              <NavLinkItem item={{
                label: "User Management",
                path: "/admin/users",
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ),
              }} />
            </>
          )}

          <Link
            to="/"
            className="group flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-300 hover:text-white hover:bg-white/5 transition-all no-underline hover:no-underline mt-4"
          >
            <span className="text-eco-400 group-hover:text-eco-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </span>
            Halaman Utama
          </Link>
        </nav>

        {/* Bottom: Logout */}
        <div className="p-4 border-t border-slate-800/60">
          <button
            onClick={handleLogout}
            className="w-full group flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-300 hover:text-white hover:bg-red-500/20 border border-transparent hover:border-red-500/30 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-50"
          >
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
              className="absolute left-0 top-0 h-full w-72 bg-gradient-to-b from-slate-900 to-slate-950 text-white shadow-2xl flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-800/60">
                <div className="flex items-center gap-3">
                  <img src={logo} alt="Defra" className="h-10 w-auto" />
                  <div>
                    <div className="font-oswald-header text-base">DEFRA</div>
                    <div className="text-xs text-slate-400 font-overpass">
                      Admin
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 pt-5 pb-2 text-[10px] font-bold tracking-widest text-slate-500 uppercase">
                Menu Utama
              </div>
              <nav className="px-3 space-y-1 flex-1 overflow-y-auto">
                {NAV_ITEMS.map((item) => (
                  <NavLinkItem key={item.path} item={item} />
                ))}

                {/* Menu Users — hanya super_admin (mobile) */}
                {currentRole === "super_admin" && (
                  <>
                    <div className="px-3 pt-4 pb-1 text-[10px] font-bold tracking-widest text-slate-600 uppercase">
                      Pengaturan
                    </div>
                    <NavLinkItem item={{
                      label: "User Management",
                      path: "/admin/users",
                      icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      ),
                    }} />
                  </>
                )}

                <Link
                  to="/"
                  onClick={() => setOpen(false)}
                  className="group flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-300 hover:text-white hover:bg-white/5 transition-all no-underline hover:no-underline mt-4"
                >
                  <span className="text-eco-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </span>
                  Halaman Utama
                </Link>
              </nav>

              <div className="p-4 border-t border-slate-800/60">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-300 hover:text-white hover:bg-red-500/20 border border-transparent hover:border-red-500/30 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            </motion.aside>
            <div
              className="absolute inset-0 -z-10 bg-slate-900/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
